import { BACKEND_URL } from '@/config';
﻿import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/context/ToastContext';
import axios from 'axios';
import {
  Plus, Search, Navigation, Download, MessageSquare, MapPin, Trash2, Pencil, Copy, Star, Eye, Ban,
  AlertTriangle, Send, Package, Weight, Calendar, Plane, RefreshCw,
} from 'lucide-react';
import MissionListCard from '@/components/MissionListCard';
import ModernAnnonceCard from '@/components/ModernAnnonceCard';
import { MissionListCardSkeleton } from '@/components/Skeletons';
import { cityFlag as flag } from '@/utils/cityFlag';
import ProposeModal from '@/components/ProposeModal';
import { tripPriceDisplay } from '@/utils/tripPriceUtils';
import { MISSIONS_API_LIMIT, buildActiveMissionIds, buildBlockingMissionTripIds } from '@/utils/missionGuards';
import { TRANSPORT_LABELS } from '@/utils/listingParsers';
import '@/styles/skeletons.css';
import { isEntreprise, isEntreprisePending } from '@/utils/entreprise';
import { PUBLISH } from '@/utils/roles';

const API = BACKEND_URL + '/api';
axios.defaults.withCredentials = true;

const TRIP_STATUS = {
  DRAFT:          { label: 'Brouillon',  tone: 'gray' },
  PENDING_REVIEW: { label: 'En attente', tone: 'blue' },
  PUBLISHED:      { label: 'Publié',     tone: 'teal' },
  ACTIVE:         { label: 'Disponible', tone: 'teal' },
  FULL:           { label: 'Complet',    tone: 'gold' },
  COMPLETED:      { label: 'Livré',      tone: 'teal' },
  EXPIRED:        { label: 'Expiré',     tone: 'gray' },
  CANCELED:       { label: 'Annulé',     tone: 'red' },
  CANCELLED:      { label: 'Annulé',     tone: 'red' },
  REJECTED:       { label: 'Rejeté',     tone: 'red' },
};

function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

const FILTERS = [
  { v: 'all',        l: 'Toutes' },
  { v: 'this_month', l: 'Ce mois' },
  { v: 'active',     l: 'En cours' },
];

const STATUS_OPTIONS = [
  { v: '',               l: 'Tous statuts' },
  { v: 'DRAFT',          l: 'Brouillon' },
  { v: 'PENDING_REVIEW', l: 'En attente' },
  { v: 'PUBLISHED',      l: 'Publié' },
  { v: 'ACTIVE',         l: 'Disponible' },
  { v: 'FULL',           l: 'Complet' },
  { v: 'EXPIRED',        l: 'Expiré' },
  { v: 'REJECTED',       l: 'Rejeté' },
  { v: 'CANCELLED',      l: 'Annulé' },
];

/* ── Helpers pour normaliser un colis public vers le format ModernAnnonceCard ── */
function daysFromNow(dateIso) {
  if (!dateIso) return '·';
  const diff = Math.ceil((new Date(dateIso) - Date.now()) / 86400000);
  if (diff < 0) return `J${diff}`;
  if (diff === 0) return 'Auj.';
  return `J-${diff}`;
}
const AV_COLORS = ['#1a4a7a', '#0e7a68', '#C8832A', '#4B44C8', '#1B4332', '#B91C1C'];
const avColor = (id) => AV_COLORS[(String(id || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0)) % AV_COLORS.length];
const initials = (f, l) => `${(f || '?')[0]}${(l || '?')[0]}`.toUpperCase();

function normalizeShipment(item) {
  const u = item.author || item.owner || {};
  const score = u.avg_rating ? u.avg_rating.toFixed(1).replace('.', ',') : null;
  return {
    raw: item, id: item.id, type: 'colis',
    from: item.departure_city, to: item.arrival_city,
    days: daysFromNow(item.departure_date),
    daysNum: !item.departure_date ? null : Math.ceil((new Date(item.departure_date) - Date.now()) / 86400000),
    price: item.price_offered,
    unit: '€',
    weight: item.weight_kg,
    hot: (() => { const d = Math.ceil((new Date(item.departure_date) - Date.now()) / 86400000); return d > 0 && d <= 5; })(),
    user: {
      id: item.user_id,
      name: `${u.first_name || 'Utilisateur'} ${(u.last_name_initial || u.last_name || '').slice(0, 1).toUpperCase()}.`.trim(),
      ini: initials(u.first_name, u.last_name_initial || u.last_name),
      color: avColor(item.user_id),
      score: score || 'Nouveau', trips: u.rating_count || 0,
      verified: u.is_verified || false, pro: isEntreprise(u), proPending: isEntreprisePending(u), subscribed: !!u.pro_subscribed,
    },
  };
}

export default function TripsPage({ myTrips = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  /* ── Onglet actif ── */
  const location = useLocation();
  const [tab, setTab] = useState(location.state?.openTab || (myTrips ? 'mine' : 'favoris'));

  /* ── Trajets (onglet Mes Trajets) ── */
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeMissionTripIds, setActiveMissionTripIds] = useState(new Set());
  const [blockingMissionTripIds, setBlockingMissionTripIds] = useState(new Set());
  const [statusFilter, setStatusFilter] = useState('');
  const [presetFilter, setPresetFilter] = useState('all');
  const [proposeTarget, setProposeTarget] = useState(null);
  const [pendingConfirm, setPendingConfirm] = useState(null); // { id, action: 'delete'|'cancel' }

  /* ── Favoris (onglet Mes Favoris) ── */
  const [favs, setFavs] = useState(() => {
    try { return JSON.parse(localStorage.getItem('c2s_favs') || '[]'); } catch { return []; }
  });
  const [favoriteShipments, setFavoriteShipments] = useState([]);
  const [loadingFavs, setLoadingFavs] = useState(false);

  const isCarrier = user?.roles?.includes('ROLE_CARRIER');

  /* ── Chargement des trajets ── */
  const load = async () => {
    setLoading(true);
    try {
      const [tripsRes, missionsRes] = await Promise.all([
        axios.get(`${API}/trips/mine`),
        axios.get(`${API}/missions?limit=${MISSIONS_API_LIMIT}`).catch(() => ({ data: { data: [] } })),
      ]);
      setTrips(tripsRes.data || []);
      const missions = missionsRes.data?.data || [];
      setActiveMissionTripIds(buildActiveMissionIds(missions, 'trip_id'));
      setBlockingMissionTripIds(buildBlockingMissionTripIds(missions));
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };

  /* ── Chargement des favoris (colis publics filtrés par localStorage) ── */
  const loadFavorites = useCallback(async () => {
    setLoadingFavs(true);
    try {
      const currentFavs = (() => { try { return JSON.parse(localStorage.getItem('c2s_favs') || '[]'); } catch { return []; } })();
      if (currentFavs.length === 0) { setFavoriteShipments([]); setFavs([]); return; }

      const [p1, p2] = await Promise.all([
        axios.get(`${API}/public/shipments?limit=50&page=1`).catch(() => ({ data: { data: [] } })),
        axios.get(`${API}/public/shipments?limit=50&page=2`).catch(() => ({ data: { data: [] } })),
      ]);
      const all = [...(p1.data?.data || []), ...(p2.data?.data || [])];
      const seen = new Set();
      const faved = all
        .filter(s => { if (currentFavs.includes(s.id) && !seen.has(s.id)) { seen.add(s.id); return true; } return false; })
        .map(normalizeShipment);
      setFavoriteShipments(faved);
      setFavs(currentFavs);
    } catch { toast.error('Erreur de chargement des favoris'); }
    finally { setLoadingFavs(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Retirer un favori ── */
  const toggleFav = useCallback((id) => {
    setFavs(prev => {
      const next = prev.filter(x => x !== id);
      localStorage.setItem('c2s_favs', JSON.stringify(next));
      return next;
    });
    setFavoriteShipments(prev => prev.filter(s => s.id !== id));
  }, []);

  useEffect(() => {
    if (tab === 'mine') load();
    else loadFavorites();
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const startOfMonth = new Date(); startOfMonth.setDate(1); startOfMonth.setHours(0, 0, 0, 0);
    return trips.filter(t => {
      if (statusFilter && t.status !== statusFilter) return false;
      if (presetFilter === 'this_month' && new Date(t.created_at || 0) < startOfMonth) return false;
      if (presetFilter === 'active' && !['ACTIVE', 'FULL'].includes(t.status)) return false;
      return true;
    });
  }, [trips, statusFilter, presetFilter]);

  const stats = useMemo(() => ({
    total:  trips.length,
    active: trips.filter(t => ['ACTIVE', 'FULL'].includes(t.status)).length,
  }), [trips]);

  const handleDelete = async (id) => {
    setPendingConfirm(null);
    try {
      await axios.delete(`${API}/trips/${id}`);
      toast.success('Trajet supprimé.');
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail || '';
      if (detail.toLowerCase().includes('mission')) {
        toast.error('Suppression impossible : une mission est en cours sur ce trajet.');
      } else {
        toast.error(detail || 'Erreur lors de la suppression');
      }
    }
  };

  const handleDuplicate = (t) => {
    sessionStorage.setItem('c2s_trip_duplicate', JSON.stringify({
      departure_city: t.departure_city,
      arrival_city: t.arrival_city,
      departure_country: t.departure_country,
      arrival_country: t.arrival_country,
      available_weight_kg: t.available_weight_kg,
      max_weight_per_parcel: t.max_weight_per_parcel,
      max_parcels: t.max_parcels,
      pricing_mode: t.pricing_mode,
      price_per_kg: t.price_per_kg,
      price_fixed: t.price_fixed,
      currencies: t.currencies,
      primary_currency: t.primary_currency,
      counter_offers: t.counter_offers,
      transport_modes: t.transport_modes,
      transport_custom: t.transport_custom,
      flex_departure: t.flex_departure,
      arrival_date: t.arrival_date,
      accepted_categories: t.accepted_categories,
      category_custom: t.category_custom,
      conditions: t.conditions,
      message_to_senders: t.message_to_senders,
      description: t.description,
    }));
    navigate('/app/trips/new?duplicate=1');
  };

  const handleExport = () => {
    const rows = [['Départ', 'Arrivée', 'Date', 'Poids dispo (kg)', 'Prix/kg (€)', 'Statut', 'Créé']];
    filtered.forEach(t => rows.push([
      t.departure_city || '', t.arrival_city || '',
      t.departure_date ? new Date(t.departure_date).toLocaleDateString('fr-FR') : '',
      t.available_weight_kg || '', t.price_per_kg || '', t.status || '',
      t.created_at ? new Date(t.created_at).toLocaleDateString('fr-FR') : '',
    ]));
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `mes-trajets-${Date.now()}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Export CSV téléchargé.');
  };

  const handleCancel = async (id) => {
    setPendingConfirm(null);
    try {
      await axios.post(`${API}/trips/${id}/cancel`);
      toast.success('Trajet annulé.');
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail || '';
      if (detail.toLowerCase().includes('mission') || detail.toLowerCase().includes('escrow') || detail.toLowerCase().includes('transit')) {
        toast.error('Annulation impossible : une livraison est en cours sur ce trajet.');
      } else {
        toast.error(detail || 'Erreur lors de l\'annulation');
      }
    }
  };

  const buildActions = (t) => {
    const acts = [];
    const hasBlockingMission = blockingMissionTripIds.has(t.id);
    const hasActiveMission = activeMissionTripIds.has(t.id);
    if (t.status === 'FULL') {
      acts.push({ key: 'msg',    icon: <MessageSquare size={16} />, label: 'Messagerie',           testid: `tc-msg-${t.id}`,    onClick: () => navigate('/app/messages') });
      acts.push({ key: 'track',  icon: <MapPin size={16} />,        label: 'Détails',              testid: `tc-track-${t.id}`,  onClick: () => navigate(`/app/trips/${t.id}`) });
      acts.push({ key: 'report', icon: <AlertTriangle size={16} />, label: 'Signaler un problème', testid: `tc-report-${t.id}`, onClick: () => navigate(`/app/trips/${t.id}#report`) });
    } else if (['ACTIVE', 'PUBLISHED'].includes(t.status)) {
      acts.push({ key: 'matchs', icon: <Search size={16} />, label: 'Voir les colis', testid: `tc-matchs-${t.id}`, onClick: () => navigate(`/app/trips/${t.id}/matching`) });
      acts.push({ key: 'edit',   icon: <Pencil size={16} />, label: 'Modifier',       testid: `tc-edit-${t.id}`,   onClick: () => navigate(`/app/trips/${t.id}/edit`) });
      acts.push({ key: 'cancel', icon: <Ban size={16} />,    label: 'Annuler',        testid: `tc-cancel-${t.id}`, onClick: () => setPendingConfirm({ id: t.id, action: 'cancel' }), disabled: hasBlockingMission, tooltip: hasBlockingMission ? 'Annulation impossible : livraison en cours' : undefined });
    } else if (t.status === 'PENDING_REVIEW') {
      acts.push({ key: 'view',   icon: <Eye size={16} />,    label: 'Voir',     testid: `tc-view-${t.id}`,   onClick: () => navigate(`/app/trips/${t.id}`) });
      acts.push({ key: 'edit',   icon: <Pencil size={16} />, label: 'Modifier', testid: `tc-edit-${t.id}`,   onClick: () => navigate(`/app/trips/${t.id}/edit`) });
      acts.push({ key: 'cancel', icon: <Ban size={16} />,    label: 'Annuler',  testid: `tc-cancel-${t.id}`, onClick: () => setPendingConfirm({ id: t.id, action: 'cancel' }), disabled: hasBlockingMission, tooltip: hasBlockingMission ? 'Annulation impossible : livraison en cours' : undefined });
    } else if (t.status === 'COMPLETED') {
      acts.push({ key: 'view', icon: <Eye size={16} />,  label: 'Voir',      testid: `tc-view-${t.id}`, onClick: () => navigate(`/app/trips/${t.id}`) });
      acts.push({ key: 'dup',  icon: <Copy size={16} />, label: 'Dupliquer', testid: `tc-dup-${t.id}`,  onClick: () => handleDuplicate(t) });
      acts.push({ key: 'rate', icon: <Star size={16} />, label: 'Avis',      testid: `tc-rate-${t.id}`, onClick: () => navigate(`/app/trips/${t.id}#rate`) });
    } else if (t.status === 'REJECTED') {
      acts.push({ key: 'view', icon: <Eye size={16} />,    label: 'Voir motif',            testid: `tc-view-${t.id}`, onClick: () => navigate(`/app/trips/${t.id}`) });
      acts.push({ key: 'dup',  icon: <Copy size={16} />,   label: 'Republier (dupliquer)', testid: `tc-dup-${t.id}`,  onClick: () => handleDuplicate(t) });
      acts.push({ key: 'del',  icon: <Trash2 size={16} />, label: 'Supprimer',             testid: `tc-del-${t.id}`,  onClick: () => setPendingConfirm({ id: t.id, action: 'delete' }), disabled: hasActiveMission, tooltip: hasActiveMission ? 'Suppression impossible : mission en cours' : undefined });
    } else if (['CANCELLED', 'CANCELED', 'EXPIRED'].includes(t.status)) {
      // Expiré (date passée, 2026-10-28) : republier le même trajet avec une nouvelle date
      if (t.status === 'EXPIRED') acts.push({ key: 'republish', icon: <RefreshCw size={16} />, label: 'Republier avec une nouvelle date', testid: `tc-republish-${t.id}`, onClick: () => navigate(`/app/trips/${t.id}/edit`) });
      acts.push({ key: 'view', icon: <Eye size={16} />,    label: 'Voir',      testid: `tc-view-${t.id}`, onClick: () => navigate(`/app/trips/${t.id}`) });
      acts.push({ key: 'dup',  icon: <Copy size={16} />,   label: 'Dupliquer', testid: `tc-dup-${t.id}`,  onClick: () => handleDuplicate(t) });
      acts.push({ key: 'del',  icon: <Trash2 size={16} />, label: 'Supprimer', testid: `tc-del-${t.id}`,  onClick: () => setPendingConfirm({ id: t.id, action: 'delete' }), disabled: hasActiveMission, tooltip: hasActiveMission ? 'Suppression impossible : mission en cours' : undefined });
    } else if (t.status === 'DRAFT') {
      acts.push({ key: 'edit', icon: <Pencil size={16} />, label: 'Modifier',      testid: `tc-edit-${t.id}`, onClick: () => navigate(`/app/trips/${t.id}/edit`) });
      acts.push({ key: 'view', icon: <Eye size={16} />,    label: 'Prévisualiser', testid: `tc-view-${t.id}`, onClick: () => navigate(`/app/trips/${t.id}`) });
      acts.push({ key: 'del',  icon: <Trash2 size={16} />, label: 'Supprimer',     testid: `tc-del-${t.id}`,  onClick: () => setPendingConfirm({ id: t.id, action: 'delete' }), disabled: hasActiveMission, tooltip: hasActiveMission ? 'Suppression impossible : mission en cours' : undefined });
    } else {
      acts.push({ key: 'view', icon: <Eye size={16} />, label: 'Voir', testid: `tc-view-${t.id}`, onClick: () => navigate(`/app/trips/${t.id}`) });
    }
    return acts;
  };

  const topbarTitle = tab === 'mine' ? 'Mes trajets' : 'Mes Favoris';
  const topbarSub = tab === 'mine'
    ? `${stats.total} trajet${stats.total > 1 ? 's' : ''} · ${stats.active} actif${stats.active > 1 ? 's' : ''}`
    : `${favoriteShipments.length} annonce${favoriteShipments.length > 1 ? 's' : ''} en favori`;

  return (
    <div>
      <div className="mlc-topbar">
        <div>
          <div className="c2s-topbar-title">{topbarTitle}</div>
          <div className="c2s-topbar-sub">{topbarSub}</div>
        </div>
        <div className="mlc-topbar-actions">
          {tab === 'mine' && (
            <button type="button" className="mlc-btn mlc-btn--ghost" onClick={handleExport} data-testid="btn-export-trips">
              <Download size={15} /> Exporter
            </button>
          )}
        </div>
      </div>

      {/* Onglets — Mes Favoris | Mes Trajets */}
      {isCarrier && (
        <div style={{ display: 'flex', gap: 2, marginBottom: 16, background: 'rgba(15,23,42,.06)', borderRadius: 8, padding: 4, width: 'fit-content' }}>
          {[['favoris', 'Mes Favoris'], ['mine', 'Mes trajets']].map(([v, l]) => (
            <button key={v} data-testid={`tab-trips-${v}`}
              style={{ padding: '6px 16px', borderRadius: 6, border: 'none', fontWeight: 500, fontSize: 12.5, cursor: 'pointer', background: tab === v ? '#fff' : 'transparent', color: tab === v ? '#111827' : '#6B7280', fontFamily: 'var(--f-body)' }}
              onClick={() => setTab(v)}>{l}
            </button>
          ))}
        </div>
      )}

      {/* ── Onglet Mes Favoris ── */}
      {tab === 'favoris' && (
        loadingFavs ? (
          <div className="mlc-grid-2" data-testid="favorites-skeleton">
            {Array.from({ length: 4 }).map((_, i) => <MissionListCardSkeleton key={i} />)}
          </div>
        ) : favoriteShipments.length === 0 ? (
          <div className="empty-state">
            <Package size={48} className="empty-state-icon" />
            <div className="empty-state-title">Aucun favori</div>
            <div className="empty-state-sub">
              Ajoutez des annonces colis en favoris depuis la page{' '}
              <span
                style={{ color: 'var(--c2s-teal, #0e7a68)', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => navigate('/app/annonces')}
              >Annonces</span>.
            </div>
          </div>
        ) : (
          <div className="mc-grid" data-testid="favorites-grid">
            {favoriteShipments.map((a, i) => (
              <ModernAnnonceCard
                key={a.id}
                a={a}
                index={i}
                isFav={favs.includes(a.id)}
                onFav={() => toggleFav(a.id)}
                onClick={() => navigate(`/app/shipments/${a.id}`)}
              />
            ))}
          </div>
        )
      )}

      {/* ── Onglet Mes Trajets ── */}
      {tab === 'mine' && (
        <>
          <div className="mlc-filters" data-testid="trips-filters">
            <div className="mlc-select-wrap">
              <select
                className="mlc-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                data-testid="filter-trip-status"
              >
                {STATUS_OPTIONS.map(opt => (
                  <option key={opt.v} value={opt.v}>{opt.l}</option>
                ))}
              </select>
            </div>
            {FILTERS.map(f => (
              <button
                key={f.v}
                type="button"
                className={`mlc-filter${presetFilter === f.v ? ' on' : ''}`}
                onClick={() => setPresetFilter(f.v)}
                data-testid={`preset-trip-${f.v}`}
              >
                {f.l}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="mlc-grid-2" data-testid="trips-skeleton">
              {Array.from({ length: 4 }).map((_, i) => <MissionListCardSkeleton key={i} />)}
            </div>
          ) : filtered.length === 0 && trips.length > 0 ? (
            // Des trajets existent, mais les filtres les masquent tous : on ne dit pas « publiez votre premier trajet »
            <div className="empty-state" data-testid="empty-filtered">
              <Navigation size={48} className="empty-state-icon" />
              <div className="empty-state-title">Aucun trajet ne correspond à ce filtre</div>
              <div className="empty-state-sub">Vous avez {trips.length} trajet{trips.length > 1 ? 's' : ''} au total.</div>
              <button className="mlc-btn mlc-btn--primary" onClick={() => { setStatusFilter(''); setPresetFilter('all'); }} data-testid="empty-reset-filters">
                Voir tous mes trajets
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="empty-state">
              <Navigation size={48} className="empty-state-icon" />
              <div className="empty-state-title">Aucun trajet pour l'instant</div>
              <div className="empty-state-sub">Publiez votre premier trajet.</div>
              {isCarrier && (
                <button className="mlc-btn mlc-btn--primary" onClick={() => navigate(PUBLISH.trajet.form)} data-testid="empty-publish-trajet">
                  <Plus size={15} /> {PUBLISH.trajet.label}
                </button>
              )}
            </div>
          ) : (
            <div className="mlc-grid-2" data-testid="trips-grid">
              {filtered.map(t => {
                const ref = `#TRJ-${(t.id || '').slice(-4).toUpperCase()}`;
                const sublineParts = [formatDate(t.updated_at || t.created_at), ref, `${t.available_weight_kg || 0} kg dispo`];
                const chips = [];
                if (t.available_weight_kg) chips.push({ icon: Weight, label: `${t.available_weight_kg} kg` });
                if (t.departure_date) chips.push({ icon: Calendar, label: formatDate(t.departure_date) });
                if (t.transport_modes?.length > 0) chips.push({ icon: Plane, label: t.transport_modes.map(m => TRANSPORT_LABELS[m] || m).join(', ') });

                const isConfirm = pendingConfirm?.id === t.id;
                return (
                  <MissionListCard
                    key={t.id}
                    testid={`trip-card-${t.id}`}
                    route={<>{flag(t.departure_city)} {t.departure_city} → {flag(t.arrival_city)} {t.arrival_city}</>}
                    subline={sublineParts.join(' · ')}
                    status={TRIP_STATUS[t.status] || { label: t.status, tone: 'gray' }}
                    chips={chips}
                    amount={tripPriceDisplay(t).amount}
                    amountUnit={tripPriceDisplay(t).unit}
                    actions={buildActions(t)}
                    onClick={() => navigate(`/app/trips/${t.id}`)}
                    confirmBand={isConfirm ? {
                      message: pendingConfirm.action === 'delete'
                        ? 'Supprimer <em>définitivement</em> ?'
                        : 'Annuler ce trajet ?',
                      variant: 'red',
                      onConfirm: () => pendingConfirm.action === 'delete' ? handleDelete(t.id) : handleCancel(t.id),
                      onCancel: () => setPendingConfirm(null),
                    } : undefined}
                  />
                );
              })}
            </div>
          )}
        </>
      )}

      <ProposeModal
        open={!!proposeTarget}
        onClose={() => setProposeTarget(null)}
        meSide="sender"
        target={proposeTarget}
        onSuccess={() => { setProposeTarget(null); toast.success('Demande envoyée.'); }}
      />
    </div>
  );
}
